import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Accordion, Card, Col, Row } from 'react-bootstrap';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';
import { FaChevronDown, FaChevronUp, FaTrashAlt } from 'react-icons/fa';

import Button from '../../../../../components/Button';
import SelectSync, { IOption } from '../../../../../components/SelectSync';
import getValidationErrors from '../../../../../utils/getValidationErrors';
import { useToast } from '../../../../../hooks/toast';
import Loading from '../../../../../components/Loading';
import api from '../../../../../services/api';
import getResponseErrors from '../../../../../utils/getResponseErrors';

interface IParceiro {
  ParCodigo: number;
  ParRazaoSocial: string;
}

interface IParceiroRelacionado {
  ParCodigo: number;
  ParRazaoSocial: string;
}

interface IFormRelacionaParceiro {
  ParCodigo: string;
}

type Props = {
  conCodigo: number;
};

const ParceiroAgendaCadastro: React.FC<Props> = ({ conCodigo }) => {
  const { addToast } = useToast();
  const formRefParceiro = useRef<FormHandles>(null);
  const [accordionAtivo, setAccordionAtivo] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [optionsParceiro, setOptionsParceiro] = useState<IOption[]>([]);
  const [parceirosRelacionados, setParceirosRelacionados] = useState<
    IParceiroRelacionado[]
  >([]);

  const carregarParceiros = useCallback(async () => {
    const response = await api.get<IParceiro[]>('/parceiros/lista');

    const options = response.data.map(parceiro => ({
      value: parceiro.ParCodigo.toString(),
      label: parceiro.ParRazaoSocial,
    }));

    setOptionsParceiro(options);
  }, []);

  const carregarParceirosRelacionados = useCallback(async () => {
    const response = await api.get<IParceiroRelacionado[]>(
      `/agendas/${conCodigo}/parceiros`,
    );

    setParceirosRelacionados(response.data);
  }, [conCodigo]);

  useEffect(() => {
    async function carregarDados(): Promise<void> {
      try {
        setLoading(true);
        await Promise.all([
          carregarParceiros(),
          carregarParceirosRelacionados(),
        ]);
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Nao foi possivel carregar parceiros da agenda',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    }

    carregarDados();
  }, [addToast, carregarParceiros, carregarParceirosRelacionados]);

  // eslint-disable-next-line @typescript-eslint/no-empty-function, prettier/prettier
  const handleOnChangeVazio = useCallback(() => { }, []);

  const handleSubmitParceiro = useCallback(
    async (dados: IFormRelacionaParceiro) => {
      try {
        formRefParceiro.current?.setErrors({});
        setLoading(true);

        const schema = Yup.object().shape({
          ParCodigo: Yup.string().required('selecione o parceiro'),
        });

        await schema.validate(dados, { abortEarly: false });

        await api.post(`/agendas/${conCodigo}/parceiro`, {
          ParCodigo: Number(dados.ParCodigo),
        });

        await carregarParceirosRelacionados();
        formRefParceiro.current?.reset();

        addToast({
          title: 'Parceiro relacionado com sucesso',
          type: 'success',
        });
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRefParceiro.current?.setErrors(errors);
          return;
        }

        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Nao foi possivel relacionar parceiro',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast, carregarParceirosRelacionados, conCodigo],
  );

  const handleEnviar = useCallback(() => {
    formRefParceiro.current?.submitForm();
  }, []);

  const handleExcluirParceiro = useCallback(
    async (parCodigo: number) => {
      try {
        setLoading(true);

        await api.delete(`/agendas/${conCodigo}/parceiro/${parCodigo}`);
        await carregarParceirosRelacionados();

        addToast({
          title: 'Parceiro removido da agenda',
          type: 'success',
        });
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Nao foi possivel remover parceiro da agenda',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast, carregarParceirosRelacionados, conCodigo],
  );

  const handleToggleAccordion = useCallback((eventKey: string | null) => {
    setAccordionAtivo(state => (state === eventKey ? null : eventKey));
  }, []);

  return (
    <>
      {loading && <Loading />}
      <hr className="mt-1 mb-3" />

      <Accordion
        activeKey={accordionAtivo || undefined}
        onSelect={handleToggleAccordion}
      >
        <Card>
          <Card.Header className="py-2">
            <Accordion.Toggle
              as="button"
              eventKey="0"
              className="btn btn-link p-0 text-left font-weight-bold text-decoration-none"
              style={{ width: '100%' }}
            >
              <span className="d-flex align-items-center justify-content-between w-100">
                <span>Parceiros da agenda</span>
                {accordionAtivo === '0' ? <FaChevronUp /> : <FaChevronDown />}
              </span>
            </Accordion.Toggle>
          </Card.Header>
          <Accordion.Collapse eventKey="0">
            <Card.Body>
              <Row noGutters>
                <Col md={12}>
                  <Form ref={formRefParceiro} onSubmit={handleSubmitParceiro}>
                    <Row className="small">
                      <Col md={9} className="form-group">
                        <SelectSync
                          name="ParCodigo"
                          label="Relacionar Parceiro"
                          placeholder="Selecione um parceiro"
                          handleSelectChange={handleOnChangeVazio}
                          options={optionsParceiro}
                        />
                      </Col>
                      <Col md={3} className="form-group">
                        <Button
                          type="button"
                          variant="success-outline"
                          className="mt-3"
                          disabled={loading}
                          onClick={() => handleEnviar()}
                        >
                          {loading ? 'adicionando...' : 'Adicionar'}
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                </Col>
              </Row>
              <Row>
                <Col md={12} lg={12}>
                  <div>
                    <table
                      className="table table-hover table-sm"
                      style={{ borderWidth: '0px' }}
                    >
                      <tbody>
                        <tr className="bg-light" style={{ fontSize: 'Small' }}>
                          <th scope="col">Parceiro</th>
                          <th scope="col">&nbsp;</th>
                        </tr>
                        {loading && (
                          <tr>
                            <td colSpan={2} className="text-center">
                              <b>carregando...</b>
                            </td>
                          </tr>
                        )}
                        {!loading && parceirosRelacionados.length === 0 && (
                          <tr>
                            <td colSpan={2} className="text-center">
                              <b>Nenhum parceiro relacionado.</b>
                            </td>
                          </tr>
                        )}
                        {!loading &&
                          parceirosRelacionados.length > 0 &&
                          parceirosRelacionados.map(parceiro => (
                            <tr
                              key={`agenda-parceiro-${parceiro.ParCodigo}`}
                              style={{ fontSize: 'Small' }}
                            >
                              <td>{parceiro.ParRazaoSocial}</td>
                              <td
                                className="text-center"
                                style={{ width: '40px' }}
                              >
                                <Button
                                  type="button"
                                  variant="secondary-outline"
                                  className="py-0"
                                  title={`${parceiro.ParCodigo}`}
                                  onClick={() => {
                                    handleExcluirParceiro(parceiro.ParCodigo);
                                  }}
                                >
                                  <FaTrashAlt />
                                </Button>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Accordion.Collapse>
        </Card>
      </Accordion>
    </>
  );
};

export default ParceiroAgendaCadastro;
